import { useCallback, useState } from "react";

import { SubscriptionFrequency, IPatient, PatientStatus, GroupedItemFlavors } from "common/types";
import { CartCreatorKind, ClientCartDetail, ICartItem } from "common/types/carts";
import { parseNumber } from "common/utils/numbers";
import { toDate, toLocaleDate } from "common/utils/dates";
import { DeliveryMethodKind } from "common/types/fulfillments";
import { deepCombineErrors } from "common/utils/errors";
import { pluralized } from "common/utils/strings";
import { indexByPrimaryKey } from "common/utils/entities";

import { CheckoutStepProps } from "client/checkout/types";
import CartItemSummary from "client/carts/components/shared/CartItemSummary";
import PatientSelectPanel, {
  Option as PatientOption,
  isPatient,
  MultiPatient,
} from "client/accounts/components/PatientSelectPanel";
import PetForm from "client/accounts/components/forms/PetForm";
import { useCreatePatientMutation } from "client/accounts/hooks";
import {
  useCartItemAssignmentMutation,
  useCartItemSubscriptionCreateUpdateMutation,
  useCartItemSubscriptionDeleteMutation,
  useCartItemUpdateMutationV2,
  useUpdateCartDeliveryMutation,
} from "client/carts/hooks";
import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import QuantityInput from "client/common/components/inputs/QuantityInput";
import { IClient } from "client/accounts/types";
import TextChip from "client/common/components/content/TextChip";
import ProductLineSelections from "client/products/components/ProductLineSelections";
import Banner from "client/common/components/content/Banner";
import Alert from "client/common/components/content/Alert";
import { CartItemRefillTooSoonSummary } from "client/carts/types";
import { useBreakpoints } from "client/common/hooks";
import PromoCodes from "client/carts/components/PromoCodes";
import { useRefillTooSoonDrawer, useRemoveCartItemDialog } from "client/checkout/stackables";
import { useInvalidateAllCartQueries } from "client/carts/hooks/invalidate";
import Select from "client/common/components/inputs/Select";
import { useAccountUpdatePetDrawer, useAccountUpdateDrawer } from "client/accounts/stackables";
import { refillTooSoonOptions } from "client/checkout/constants";

import StepLayout from "../CheckoutStepLayout";
import CheckoutSummary from "../CheckoutSummary";

import styles from "../../styles/ItemOptionsCheckoutStep.module.less";

const generateInitialAssignmentOptions = (patients: IPatient[]): Record<number, number> =>
  patients.reduce((obj, pet) => ({ ...obj, [pet.id]: 0 }), {});

type CartItemAssignmentModalProps = {
  cartItem: ICartItem;
  cart: ClientCartDetail;
  client: IClient;
  isVisible: boolean;
  onClose: () => void;
};

const CartItemAssignmentModal = ({
  isVisible,
  cart,
  cartItem,
  client,
  onClose,
}: CartItemAssignmentModalProps) => {
  const [assignments, setAssignments] = useState<Record<number, number>>(
    generateInitialAssignmentOptions(client.patients),
  );
  const totalAssigned = Object.values(assignments).reduce((val, assignment) => val + assignment, 0);

  const handleClose = () => {
    setAssignments(generateInitialAssignmentOptions(client.patients));
    onClose();
  };

  const {
    mutate: assignCartItems,
    isLoading,
    error,
  } = useCartItemAssignmentMutation(cart.id, {
    onSuccess: handleClose,
  });

  const onSubmit = () => {
    const body = {
      cart_item_id: cartItem.id,
      assignments: Object.entries(assignments).map(([patientId, quantity]) => ({
        patient_id: parseNumber(patientId),
        quantity,
      })),
    };
    assignCartItems(body);
  };

  return (
    <Modal visible={isVisible} onClose={handleClose}>
      <ModalLayout>
        <ModalLayout.Header title="Select dose per pet" onClose={handleClose} />
        <ModalLayout.Form
          submitText="Update dose"
          canSubmit={totalAssigned === cartItem?.quantity}
          onSubmit={onSubmit}
          isLoading={isLoading}
          error={error}
        >
          {client.patients.map((p) => (
            <ModalLayout.Form.Group key={p.id}>
              <div className={styles.dose}>
                <div>{p.name_display}</div>
                <QuantityInput
                  onChange={(val) => setAssignments({ ...assignments, [p.id]: val })}
                  value={assignments[p.id] ?? 0}
                  min={0}
                  max={cartItem.quantity - totalAssigned + (assignments[p.id] ?? 0)}
                />
              </div>
            </ModalLayout.Form.Group>
          ))}
        </ModalLayout.Form>
      </ModalLayout>
    </Modal>
  );
};

type AddPatientModalProps = {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: (patient: IPatient) => void;
};

const AddPatientModal = ({ isVisible, onClose, onSuccess }: AddPatientModalProps) => {
  const {
    mutate: createPatient,
    isLoading,
    error,
  } = useCreatePatientMutation({
    onSuccess: (patient) => {
      onClose();
      onSuccess(patient);
    },
  });

  return (
    <Modal visible={isVisible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header title="Add pet" onClose={onClose} />
        <PetForm
          isLoading={isLoading}
          submitText="Add Pet"
          error={error}
          onSubmit={(values) =>
            createPatient({
              ...values,
              weight_in_units: parseNumber(values.weight_in_units, 0) || null,
              weight_unit_of_measure: values.weight_unit_of_measure,
              birth_day: parseNumber(values.birth_day, 0) || null,
              birth_month: parseNumber(values.birth_month, 0) || null,
              birth_year: parseNumber(values.birth_year, 0) || null,
              primary_breed: null,
              secondary_breeds: [],
              color: null,
              custom_breed: "",
              custom_species: "",
              status: PatientStatus.Active,
              image_id: values.image?.id ?? null,
            })
          }
        />
      </ModalLayout>
    </Modal>
  );
};

const MULTI_PATIENT_OPTION: MultiPatient = { multi: true };

type CartItemSelectionsProps = {
  cartItem: ICartItem;
  refillTooSoonSummary: CartItemRefillTooSoonSummary | undefined;
  cart: ClientCartDetail;
  client: IClient;
  heading: string;
};

const CartItemSelections = ({
  cartItem,
  refillTooSoonSummary,
  cart,
  client,
  heading,
}: CartItemSelectionsProps) => {
  const [isAddPatientModalVisible, setIsAddPatientModalVisible] = useState(false);
  const [isSplitPatientModalVisible, setIsSplitPatientModalVisible] = useState(false);

  const { open: openRemoveCartItemDialog } = useRemoveCartItemDialog();
  const { open: openRefillTooSoonDrawer } = useRefillTooSoonDrawer();

  const itemErrorActions = [
    {
      label: "Remove item",
      onClick: () => openRemoveCartItemDialog({ cartId: cart.id, cartItem }),
    },
  ];

  const { isPhoneAndSmaller } = useBreakpoints();

  const { mutate: updateCartDelivery, error: updateCartDeliveryError } =
    useUpdateCartDeliveryMutation(cart.id);

  const { mutate: updateCartItemPatient, error: updateCartItemPatientError } =
    useCartItemUpdateMutationV2(cartItem.id, cart.id);

  const { mutate: updateCartItemSelections, error: updateCartItemSelectionsError } =
    useCartItemUpdateMutationV2(cartItem.id, cart.id);

  const { mutate: updateCartItemQuantity, error: updateCartItemQuantityError } =
    useCartItemUpdateMutationV2(cartItem.id, cart.id);

  const { mutate: createUpdateSubscription, error: createUpdateSubscriptionError } =
    useCartItemSubscriptionCreateUpdateMutation(cart.id, cartItem.id);

  const { mutate: deleteSubscription, error: deleteSubscriptionError } =
    useCartItemSubscriptionDeleteMutation(cart.id, cartItem.id);

  const invalidateCartQueries = useInvalidateAllCartQueries();

  const subscriptionError = createUpdateSubscriptionError || deleteSubscriptionError;

  const selectPatient = useCallback(
    (patient: IPatient) => {
      updateCartItemPatient({
        body: { patient: patient.id },
        optimisticData: { patient },
      });
    },
    [updateCartItemPatient],
  );

  const selectPickup = (isPickup: boolean) => {
    const updates = [
      {
        cart_item_id: cartItem.id,
        is_pickup: isPickup,
      },
    ];
    const newCartItems = cart.items.map((ci) =>
      ci.id === cartItem.id ? { ...ci, is_pickup: isPickup } : ci,
    );

    updateCartDelivery({ body: updates, optimisticData: { newCartItems } });
  };

  const selectSubscription = (frequency: SubscriptionFrequency | null) => {
    if (!frequency) {
      deleteSubscription();
    } else {
      createUpdateSubscription(frequency);
    }
  };

  const renderPrescriptionTag = (patientOption: PatientOption) => {
    if (!isPatient(patientOption)) {
      return null;
    }
    const activePrescription = cartItem.available_prescriptions.find(
      (prescription) => prescription.patient.id === patientOption.id,
    );

    if (!activePrescription) {
      return null;
    }

    return (
      <TextChip size="s" variant="primary">
        Rx on file
      </TextChip>
    );
  };

  const onPetUpdateSuccess = () => {
    invalidateCartQueries();
  };

  const patientOptions = [
    ...client.patients,
    ...(cartItem.quantity > 1 && client.patients.length > 1 ? [MULTI_PATIENT_OPTION] : []),
  ];

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    weekday: "long",
    month: "long",
    day: "numeric",
  };

  // we handle clinic-created carts slightly differently under the assumption that
  // clients will enter the checkout flow from the checkout magic link (and not see a cart)
  const isClinicCreatedCart = cart.creator_type === CartCreatorKind.Clinic;

  const flavorSelection = cartItem.flavor_selection ?? null;
  const flavorSelectionOptionsGroup: GroupedItemFlavors | null = cartItem.item.flavor_options
    ?.length
    ? cartItem.item.flavor_options[0]
    : null;

  const onSelectionChange = (val: string) => {
    const newSelectionId = parseNumber(val, 0);
    if (flavorSelection && newSelectionId === flavorSelection) {
      return;
    }
    const newFlavorSelection = flavorSelectionOptionsGroup?.options.find(
      (s) => s.id === newSelectionId,
    );
    if (!newFlavorSelection) {
      return;
    }
    updateCartItemSelections({
      body: { flavor_selection: newFlavorSelection?.id },
      optimisticData: {
        flavor_selection: newFlavorSelection,
      },
    });
  };

  const { open: openAccountPetUpdate } = useAccountUpdatePetDrawer();
  const petId = cartItem.patient?.id ?? 0;

  const steps = [
    {
      visible: true,
      title: `Select ${pluralized(cartItem.quantity, "Pet")} for this medication`,
      content: (
        <>
          {updateCartItemPatientError && (
            <StepLayout.FetchErrorAlert
              error={updateCartItemPatientError}
              fieldErrors={[deepCombineErrors(updateCartItemPatientError.errors)]}
              actions={itemErrorActions}
            />
          )}
          {!!cartItem.missing_required_patient_fields_message && (
            <Alert
              variant="danger"
              shouldScrollOnMount={true}
              actions={[
                {
                  label: "Update pet",
                  onClick: () => openAccountPetUpdate({ id: petId, onSuccess: onPetUpdateSuccess }),
                  hasRightIcon: true,
                },
              ]}
            >
              {cartItem.missing_required_patient_fields_message}
            </Alert>
          )}
          {!!cartItem.invalid_patient_species_message && (
            <Alert variant="warning" shouldScrollOnMount={true} actions={undefined}>
              {cartItem.invalid_patient_species_message}
            </Alert>
          )}
          {!!cartItem.invalid_patient_weight_message && (
            <Alert variant="warning" shouldScrollOnMount={true} actions={undefined}>
              {cartItem.invalid_patient_weight_message}
            </Alert>
          )}
          <StepLayout.Section>
            <PatientSelectPanel
              options={patientOptions}
              value={cartItem.patient}
              onSelect={selectPatient}
              onSelectMulti={() => setIsSplitPatientModalVisible(true)}
              hasAdd={true}
              onAdd={() => setIsAddPatientModalVisible(true)}
              renderTag={renderPrescriptionTag}
            />
          </StepLayout.Section>
        </>
      ),
    },
    ...(flavorSelectionOptionsGroup
      ? [
          {
            // allow selections in clinic-created cart checkouts because the client
            // did not have a change to choose on PDP
            visible: isClinicCreatedCart,
            title: `Choose your ${flavorSelectionOptionsGroup.label.toLowerCase()}`,
            content: (
              <Select
                label={flavorSelectionOptionsGroup.label}
                value={flavorSelection ? `${flavorSelection}` : ""}
                onChange={onSelectionChange}
              >
                {flavorSelectionOptionsGroup.options.map((option) => (
                  <Select.Option key={option.id} value={`${option.id}`}>
                    {option.display}
                  </Select.Option>
                ))}
              </Select>
            ),
          },
        ]
      : []),
    {
      visible: !cartItem.is_available || cartItem.fulfillment_options.length > 1,
      title: "Choose my fulfillment source",
      content: (
        <>
          {updateCartDeliveryError && (
            <StepLayout.FetchErrorAlert
              error={updateCartDeliveryError}
              actions={itemErrorActions}
            />
          )}
          <StepLayout.Section>
            {cartItem.is_available ? (
              <ProductLineSelections.DeliveryMethodSelectPanel
                value={cartItem.is_pickup ? DeliveryMethodKind.Pickup : DeliveryMethodKind.Shipping}
                onChange={(val) => selectPickup(val === DeliveryMethodKind.Pickup)}
                fulfillmentOptions={cartItem.fulfillment_options}
              />
            ) : (
              <Alert
                variant="danger"
                shouldScrollOnMount={true}
                actions={[
                  {
                    label: "Remove item",
                    onClick: () => openRemoveCartItemDialog({ cartId: cart.id, cartItem }),
                  },
                ]}
              >
                This item is currently unavailable.
              </Alert>
            )}
          </StepLayout.Section>
        </>
      ),
    },
    {
      visible: cartItem.is_subscribable,
      title: "Subscribe and Save",
      content: (
        <>
          {subscriptionError && (
            <StepLayout.FetchErrorAlert
              error={subscriptionError}
              fieldErrors={[deepCombineErrors(subscriptionError.errors)]}
              actions={itemErrorActions}
            />
          )}
          <StepLayout.Section>
            <ProductLineSelections.SubscriptionSelectPanel
              item={{
                ...cartItem,
                recommended_frequency: cartItem.recommended_frequency,
                eligible_for_autoship_discounts: cartItem.item.eligible_for_autoship_discounts,
              }}
              onChange={selectSubscription}
              value={
                cartItem.subscription
                  ? { ...cartItem.subscription, display: cartItem.subscription.frequency_display }
                  : null
              }
              quantity={cartItem.quantity}
            />
            {cartItem.existing_subscription_modifications && (
              <Banner>
                <Banner.Content
                  label="Your existing Autoship for this item will be updated to match these new preferences:"
                  description={
                    <div>
                      {cartItem.existing_subscription_modifications.next_order_date && (
                        <li>
                          Next order date:{" "}
                          {toDate(cartItem.existing_subscription_modifications.next_order_date)}
                        </li>
                      )}
                      {cartItem.existing_subscription_modifications.frequency && (
                        <li>Frequency: {cartItem.existing_subscription_modifications.frequency}</li>
                      )}
                      {cartItem.existing_subscription_modifications.quantity && (
                        <li>Quantity: {cartItem.existing_subscription_modifications.quantity}</li>
                      )}
                      {cartItem.existing_subscription_modifications.payment_method && (
                        <li>
                          Payment: {cartItem.existing_subscription_modifications.payment_method}
                        </li>
                      )}
                    </div>
                  }
                />
              </Banner>
            )}
          </StepLayout.Section>
        </>
      ),
    },
  ].filter((step) => step.visible);

  return (
    <StepLayout.Item>
      <StepLayout.Header>
        <StepLayout.Heading>{heading}</StepLayout.Heading>
      </StepLayout.Header>
      {updateCartItemQuantityError && (
        <StepLayout.FetchErrorAlert
          error={updateCartItemQuantityError}
          fieldErrors={[deepCombineErrors(updateCartItemQuantityError.errors)]}
          actions={itemErrorActions}
        />
      )}
      {updateCartItemSelectionsError && (
        <StepLayout.FetchErrorAlert
          error={updateCartItemSelectionsError}
          fieldErrors={[deepCombineErrors(updateCartItemSelectionsError.errors)]}
          actions={itemErrorActions}
        />
      )}
      <StepLayout.Item.Info>
        <div className={styles.cartItemSummaryContainer}>
          <CartItemSummary
            cartItem={cartItem}
            hasPatient={false}
            hasSelections={!isClinicCreatedCart}
            hasFrequency={false}
            canUpdateQuantity={true}
            onUpdateQuantity={(quantity) => updateCartItemQuantity({ body: { quantity } })}
            canRemoveItem={true}
            hasPrice={isPhoneAndSmaller}
            onRemoveItem={() => openRemoveCartItemDialog({ cartId: cart.id, cartItem })}
          />
          {!cartItem.refill_too_soon_reason &&
            refillTooSoonSummary &&
            !refillTooSoonSummary.is_valid_by_refill_too_soon && (
              <Alert
                variant="danger"
                actions={[
                  {
                    label: "I have a special circumstance",
                    onClick: () => openRefillTooSoonDrawer({ cartId: cart.id, cartItem }),
                    hasRightIcon: true,
                  },
                ]}
              >
                <div className={styles.alertRTS}>
                  It&apos;s too soon to order a refill! Please remove item until it is available for
                  order
                  {refillTooSoonSummary.earliest_date_to_refill
                    ? ` beginning on ${toLocaleDate(
                        refillTooSoonSummary.earliest_date_to_refill,
                        dateFormatOptions,
                      )}`
                    : ""}
                  .
                </div>
              </Alert>
            )}
          {!!cartItem.refill_too_soon_reason && (
            <Alert
              actions={[
                {
                  label: "Edit circumstance",
                  onClick: () => openRefillTooSoonDrawer({ cartId: cart.id, cartItem }),
                  hasRightIcon: true,
                },
              ]}
            >
              Special circumstance for early refill:{" "}
              {refillTooSoonOptions[cartItem.refill_too_soon_reason]}
            </Alert>
          )}
        </div>
      </StepLayout.Item.Info>
      <StepLayout.Item.Content>
        {steps.map(({ title, content }, index) => (
          <StepLayout.Section key={title}>
            <StepLayout.Header>
              <StepLayout.Heading>
                {steps.length > 1 && `${index + 1}. `}
                {title}
              </StepLayout.Heading>
            </StepLayout.Header>
            {content}
          </StepLayout.Section>
        ))}
      </StepLayout.Item.Content>
      <AddPatientModal
        isVisible={isAddPatientModalVisible}
        onSuccess={selectPatient}
        onClose={() => setIsAddPatientModalVisible(false)}
      />
      <CartItemAssignmentModal
        cartItem={cartItem}
        cart={cart}
        client={client}
        isVisible={isSplitPatientModalVisible}
        onClose={() => setIsSplitPatientModalVisible(false)}
      />
    </StepLayout.Item>
  );
};

type ItemOptionsCheckoutStepProps = CheckoutStepProps;

const ItemOptionsCheckoutStep = (props: ItemOptionsCheckoutStepProps) => {
  const { client, cart, onPrevious, onNext, isValid, isLoading, refillTooSoonSummary } = props;

  const isClinicCreatedCart = cart.creator_type === CartCreatorKind.Clinic;

  const cartItemIdToRefillTooSoonSummary: Partial<
    Record<number, CartItemRefillTooSoonSummary | undefined>
  > = indexByPrimaryKey(refillTooSoonSummary.cart_items, "id");

  const invalidateCartQueries = useInvalidateAllCartQueries();

  const onAccountUpdateSuccess = () => {
    invalidateCartQueries();
  };

  const { open: openAccountUpdate } = useAccountUpdateDrawer();

  return (
    <StepLayout>
      <StepLayout.Section>
        {!!cart.missing_required_client_fields_message && (
          <Alert
            variant="danger"
            shouldScrollOnMount={true}
            actions={[
              {
                label: "Update",
                /* TODO: [sc-41928] add new checkout profile update form here */
                onClick: () =>
                  openAccountUpdate({ updatePhoneNumber: true, onSuccess: onAccountUpdateSuccess }),
                hasRightIcon: true,
              },
            ]}
          >
            {cart.missing_required_client_fields_message}
          </Alert>
        )}
        <StepLayout.Title>Cart Items</StepLayout.Title>
        <StepLayout.Section>
          {cart.items.map((cartItem, index) => (
            <CartItemSelections
              key={cartItem.id}
              cartItem={cartItem}
              refillTooSoonSummary={cartItemIdToRefillTooSoonSummary[cartItem.id]}
              cart={cart}
              client={client}
              heading={`Item ${index + 1} of ${cart.items.length}`}
            />
          ))}
        </StepLayout.Section>
      </StepLayout.Section>
      <div className={styles.priceSummaryContainer}>
        <StepLayout.Section>
          <div className={styles.priceSummary}>
            <StepLayout.Title>Order Summary</StepLayout.Title>
            <PromoCodes cart={cart} />
            <CheckoutSummary.PriceSummary cart={cart} />
          </div>
        </StepLayout.Section>
      </div>
      <StepLayout.Actions>
        <StepLayout.ActionButton.Previous onClick={onPrevious}>
          {/* no concept of "returning to cart" for client when it was a clinic-created cart */}
          {isClinicCreatedCart ? "Continue Shopping" : "Return to Cart"}
        </StepLayout.ActionButton.Previous>
        <StepLayout.ActionButton.Next
          isLoading={isLoading}
          onClick={onNext}
          disabled={!isValid}
          disabledHelp={
            cart.warning_messages.length
              ? "Please fix the issues with your cart items before proceeding."
              : "Please fill out all fields and fix any issues before proceeding."
          }
        >
          Continue to Shipping
        </StepLayout.ActionButton.Next>
      </StepLayout.Actions>
    </StepLayout>
  );
};

export default ItemOptionsCheckoutStep;
